import { useNavigate } from 'react-router';
import { useProjects } from '@t90/api-client';
import { DataTable, Button, Badge, Plus } from '@t90/ui';
import { formatDate, formatNumber } from '@t90/common';
import type { ColumnDef } from '@tanstack/react-table';
import type { Project } from '@t90/common';

const columns: ColumnDef<Project>[] = [
  {
    accessorKey: 'project_number',
    header: 'Project #',
    cell: ({ row }) => (
      <span className="font-medium">{row.getValue('project_number')}</span>
    ),
  },
  {
    accessorKey: 'customer',
    header: 'Customer',
    cell: ({ row }) => {
      const order = (row.original as any).order;
      return order?.customer?.customer_name ?? 'Unknown';
    },
  },
  {
    id: 'service',
    header: 'Service',
    cell: ({ row }) => {
      const serviceItem = (row.original as any).service_item;
      return serviceItem?.service_item_name ?? '-';
    },
  },
  {
    accessorKey: 'project_status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.getValue('project_status') as string;
      return <Badge variant={getStatusVariant(status)}>{status}</Badge>;
    },
  },
  {
    id: 'progress',
    header: 'Progress',
    cell: ({ row }) => {
      const completed = row.original.units_completed ?? 0;
      const total = row.original.quantity ?? 0;
      const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
      return (
        <div className="flex items-center gap-2">
          <div className="h-2 w-24 rounded-full bg-muted">
            <div
              className="h-2 rounded-full bg-primary"
              style={{ width: `${percent}%` }}
            />
          </div>
          <span className="text-xs text-muted-foreground">{percent}%</span>
        </div>
      );
    },
  },
  {
    accessorKey: 'due_date',
    header: 'Due Date',
    cell: ({ row }) => {
      const date = row.getValue('due_date') as string | null;
      return date ? formatDate(date) : '-';
    },
  },
];

function getStatusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' {
  switch (status) {
    case 'Not Started':
      return 'secondary';
    case 'In Progress':
      return 'default';
    case 'Completed':
      return 'success';
    case 'On Hold':
      return 'warning';
    default:
      return 'secondary';
  }
}

export function ProjectsPage() {
  const navigate = useNavigate();
  const { data: projects, isLoading } = useProjects();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Projects</h1>
          <p className="text-muted-foreground">
            Track project progress and manage work assignments
          </p>
        </div>
        <Button onClick={() => navigate('/projects/new')}>
          <Plus className="mr-2 h-4 w-4" />
          New Project
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={projects ?? []}
        searchKey="project_number"
        searchPlaceholder="Search projects..."
        isLoading={isLoading}
        onRowClick={(row) => navigate(`/projects/${row.id}`)}
        emptyMessage="No projects found. Create your first project to get started."
      />
    </div>
  );
}
